import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import './App.css';
import { Fonts } from './fonts';
import SponsorForm from './components/SponsorForm';
import EventsForm from './components/EventsForm';

// Add this style to your component
const fontFaceStyle = `
  @font-face {
    font-family: 'Russian2022';
    src: url('${Fonts.Russian2022}') format('truetype');
    font-weight: normal;
    font-style: normal;
  }
`;

function MainContent() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    // Inject the font-face style
    const style = document.createElement('style');
    style.textContent = fontFaceStyle;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    // Function to get all images from the events folder
    const importAll = (r) => r.keys().map(r);
    try {
      // Import all images from the events folder
      const images = importAll(require.context('../public/events', false, /\.(png|jpe?g|svg)$/i));
      setSlides(images);
    } catch (error) {
      console.error('Error loading event images:', error);
    }
  }, []);

  useEffect(() => {
    if (slides.length === 0) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prevSlide) => 
        prevSlide === slides.length - 1 ? 0 : prevSlide + 1
      );
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prevSlide) => 
      prevSlide === slides.length - 1 ? 0 : prevSlide + 1
    );
  };

  const prevSlide = () => {
    setCurrentSlide((prevSlide) => 
      prevSlide === 0 ? slides.length - 1 : prevSlide - 1
    );
  };

  const testimonials = [
    {
      content: "Visible Women gave me the community I desperately needed during a classic early 20s time of extreme loneliness, insecurity, and a profoundly tangible sense of lostness. From the first event, I felt like I was at the right place at the right time. Since then, Stacey has created an incredible space for me to share my story and connect with so many women who are so beautifully special to me.",
      author: "Synclaire Warren",
      title: "UN Keynote Speaker",
      image: "/synclaire.png"
    },
    {
      content: "The connections I've made through Visible Women and Stacey have changed my life and afforded me the opportunity to pursue my dreams of making movies! The Visible Women talking circles will change your life - affording you the opportunity to connect with brilliant diverse women from across the globe.",
      author: "Julia Rae",
      title: "Filmmaker & Creator",
      image: "/julia.jpeg"
    },
    {
      content: "Visible Women, by definition is an agency committed to creating opportunities for women, by women, where each have a seat at the table. Every event, at all introductions, each woman is seen and acknowledged; capable to grow. How beautiful it is to be a Visible Women today, always.",
      author: "Orane Abezis",
      title: "Design Director At B&B ITALIA USA",
      image: "/orane.jpg"
    },
    {
      content: "Stacey's Visible Women events facilitate kismet moments where engaging discussions and meaningful connections are made. The energy in the room feels so positive with so many smiling faces. I feel inspired and eager for the next event!",
      author: "Francesca Vuillemin",
      title: "Astrologer, Sustainability Advocate & Model",
      image: "/francesca.png"
    },
    {
      content: "Visible Women is an incredible space for powerful intentional strong women in all fields to connect and raise each other up in a non pressured environment. It's beautiful to watch and participate in this unique non hierarchical meeting of minds and hearts. Truly special. Not to be missed!!",
      author: "Lily Leah",
      title: "Creative Consultant",
      image: "/lily.png"
    },
    {
      content: "In my early career, I spent 20 years in fashion advertising. Now, as an illustrator/artist working remotely, I find that the themes discussed in the circle—especially around emerging with fearlessness—spoke volumes to my own journey with regard to becoming 'visible'. The intersection of fashion and fearlessness is a subject that feels so deep and layered. Thank you for hosting such an enriching and thought-provoking event.",
      author: "Donna Wilson",
      title: "Artist & Illustrator @99percentinspired",
      image: "/donna.png"
    },
    {
      content: "Last night was absolutely wonderful. I had so many takeaways from the talking circle and loved everything about the evening flow & vibe. My friends had a great time & I loved seeing you! Thank you for including me!",
      author: "Izze Stein",
      title: "Singer/Songwriter",
      image: "/izze.png"
    },
    {
      content: "Every Visible Women talking circle is unforgettable. Stacey creates a safe and curated space to connect with individuals who are unique yet bonded by strength, innovation, creativity, and womanhood. I am so thankful to have found this community.",
      author: "Kayla Ehle",
      title: "Actor & Model",
      image: "/kayla.png"
    },
    {
      content: "Stacey has this incredible way of bringing people together—every time I walk into a Visible Women gathering, I know I'm in for something special. She creates space for real conversations, thoughtful connections, and those small but powerful moments that stay with you long after. There's an energy about her that just pulls you in—warm, kind, and full of fun. She makes sure everyone feels seen, heard, and valued, and somehow, you always leave feeling lighter, inspired, and excited about what's possible. I was honored to be included in her most recent event and wow, was it magical! In a time when community is more important than ever, VW is one I am grateful to be a part of and fully stand behind. And knowing Stacey is a gift—she's a force in the best way, leaving a joyful imprint on your heart. Can't wait for the next one!",
      author: "Kezi Ban",
      title: "Photographer & Director",
      image: "/kezi.png"
    }
  ];

  // Add auto-scroll effect for testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => 
        prev === testimonials.length - 1 ? 0 : prev + 1
      );
    }, 8000); // Change testimonial every 8 seconds for better readability

    return () => clearInterval(timer);
  }, [testimonials.length]);

  const renderSlideshow = () => {
    if (slides.length === 0) return null;

    return (
      <section className="events-showcase">
        <div className="events-content">
          <h2>We Don't Just Introduce... We Create Opportunities</h2>
          <p className="events-description">
            Discover our curated gatherings where influential voices and visionary brands 
            connect in extraordinary settings. Each event is thoughtfully designed to foster 
            authentic relationships and create memorable moments that drive meaningful partnerships.
          </p>
        </div>
        <div className="slideshow-container">
          <div className="slides" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
            {slides.map((slide, index) => (
              <div key={index} className="slide">
                <img src={slide} alt={`Event showcase ${index + 1}`} />
                <div className="slide-overlay"></div>
              </div>
            ))}
          </div>
          
          {slides.length > 1 && (
            <>
              <button className="slide-arrow prev" onClick={prevSlide}>
                <svg viewBox="0 0 24 24">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button className="slide-arrow next" onClick={nextSlide}>
                <svg viewBox="0 0 24 24">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>

              <div className="slide-indicators">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    className={`slide-indicator ${currentSlide === index ? 'active' : ''}`}
                    onClick={() => setCurrentSlide(index)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="experience-benefits">
          <div className="benefit-item">
            <h3>Curated Experiences</h3>
            <p>Host tailored in-person events, talking circles, workshops, or networking sessions where top influencers meet your team and explore synergies.</p>
          </div>
          
          <div className="benefit-item">
            <h3>Handpicked Talent</h3>
            <p>Connect with women who embody your brand values and have the authority to drive your message.</p>
          </div>
          
          <div className="benefit-item">
            <h3>Growth-Focused Results</h3>
            <p>Our partnerships are designed to increase visibility, brand credibility, and customer engagement.</p>
          </div>
        </div>
      </section>
    );
  };

  const handleScroll = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    const navbarHeight = document.querySelector('.navbar').offsetHeight;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  };

  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-container pill-container">
          <div className="logo">VISIBLE WOMEN AGENCY</div>
          <div className="nav-links">
            <a href="#about" onClick={(e) => handleScroll(e, 'about')}>About</a>
            <a href="#experiences" onClick={(e) => handleScroll(e, 'experiences')}>Experiences</a>
            <a href="#press" onClick={(e) => handleScroll(e, 'press')}>As featured in</a>
          </div>
        </div>
      </nav>

      <section className="hero">
        <img className="background-video" src="/20250128_185659.jpeg" alt="Background" />
        
        <div className="ray-animation">
          <div className="ray ray-1"></div>
          <div className="ray ray-2"></div>
          <div className="ray ray-3"></div>
          <div className="ray ray-4"></div>
          <div className="ray ray-5"></div>
        </div>
        
        <div className="hero-content">
          <h1 className="mega-title">
            <span>UNLOCK</span>
            <span>GROWTH</span>
            <span>THROUGH</span>
            <span>POWERFUL</span>
            <span className="highlight">CONNECTIONS</span>
          </h1>
          <div className="cta-buttons">
            <button 
              onClick={() => navigate('/become-sponsor')}
              className="cta-button primary"
            >
              WORK WITH US
            </button>
            <button 
              onClick={() => navigate('/attend-events')}
              className="cta-button secondary"
            >
              ATTEND OUR EVENTS
            </button>
          </div>
        </div>
      </section>

      <section id="about" className="connection-section">
        <div className="connection-content">
          <h2>Connections That Drive Growth, Powered by Influential Women</h2>
          <p>Access our curated network of influential women with millions of combined followers. 
          We bridge the gap between brands and creators through bespoke in-person experiences, 
          fostering authentic collaborations that drive meaningful growth.</p>
        </div>
        <div className="trusted-brands">
          <p className="trusted-text">COLLABORATING WITH LEADING BRANDS WORLDWIDE</p>
          <div className="brands-grid">
            <img src="/partners/penbrush.png" alt="Pen + Brush" className="brand-logo" />
            <img src="/partners/sohohouse.png" alt="Soho House" className="brand-logo" />
            <img src="/partners/thewell.png" alt="The Well" className="brand-logo" />
            <img src="/partners/dvf.png" alt="Diane Von Furstenberg" className="brand-logo" />
            <img src="/partners/zadig.png" alt="Zadig & Voltaire" className="brand-logo" />
          </div>
        </div>
      </section>

      <section id="experiences">
        {renderSlideshow()}
      </section>

      <section id="press" className="press-section">
        <h2>As featured in</h2>
        <div className="press-container">
          <div className="press-item">
            <p className="press-quote">
              Visible Women's clear directive is that women must have seats at the table
            </p>
            <img src="/press/forbes.png" alt="Forbes" className="press-logo" />
          </div>
          
          <div className="press-item">
            <p className="press-quote">
              Visible Women has been successfully hosting important conversations, connecting and collaborating with trailblazing entrepreneurs and creators
            </p>
            <img src="/press/newyorker.png" alt="The New Yorker" className="press-logo" />
          </div>
          
          <div className="press-item">
            <p className="press-quote">
              Women to absolutely follow
            </p>
            <img src="/press/madame.png" alt="Madame Figaro" className="press-logo" />
          </div>
        </div>
      </section>

      <section className="testimonials-section">
        <div className="testimonials-container">
          <div className="testimonials-header">
            <h2>There is a reason people are raving about us</h2>
          </div>
          <div className="testimonials-carousel">
            <div className="testimonials-track">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={index} 
                  className={`testimonial-card ${currentTestimonial === index ? 'active' : ''}`}
                  style={{
                    opacity: currentTestimonial === index ? 1 : 0,
                    transform: `translateX(${(index - currentTestimonial) * 100}%) scale(${currentTestimonial === index ? 1 : 0.95})`,
                    visibility: Math.abs(currentTestimonial - index) <= 1 ? 'visible' : 'hidden'
                  }}
                >
                  <div className="testimonial-content">
                    {testimonial.content}
                  </div>
                  <div className="testimonial-author">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.author} 
                      className="author-image" 
                    />
                    <div className="author-info">
                      <h4>{testimonial.author}</h4>
                      <p>{testimonial.title}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="carousel-indicators">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-indicator ${currentTestimonial === index ? 'active' : ''}`}
                  onClick={() => setCurrentTestimonial(index)}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Build Your Network and Grow Your Brand?</h2>
          <button 
            onClick={() => navigate('/become-sponsor')} 
            className="cta-button"
          >
            Work With Us
          </button>
        </div>
      </section>

      <section className="partners-section">
        <h2>Visible Women Experiences have been hosted at:</h2>
        <div className="partners-grid">
          <img src="/partners/penbrush.png" alt="Pen + Brush" className="partner-logo" />
          <img src="/partners/sohohouse.png" alt="Soho House" className="partner-logo" />
          <img src="/partners/thewell.png" alt="The Well" className="partner-logo" />
          <img src="/partners/dvf.png" alt="Diane Von Furstenberg" className="partner-logo" />
          <img src="/partners/zadig.png" alt="Zadig & Voltaire" className="partner-logo" />
        </div>
      </section>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-social">
            <a href="https://www.instagram.com/visiblewomenagency/" target="_blank" rel="noopener noreferrer" className="social-link">
              <svg className="social-icon" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a href="https://www.linkedin.com/in/stacey-yael-esq-ba1881130/" target="_blank" rel="noopener noreferrer" className="social-link">
              <svg className="social-icon" viewBox="0 0 24 24">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </svg>
            </a>
            <a href="https://open.spotify.com/show/4gXkKY3vMoWtA7FIhFnmWv" target="_blank" rel="noopener noreferrer" className="social-link">
              <svg className="social-icon" viewBox="0 0 24 24">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
              </svg>
            </a>
            <a href="mailto:stacey@visiblewomenagency.com" className="social-link">
              <svg className="social-icon" viewBox="0 0 24 24">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
            </a>
          </div>
          <div className="footer-divider"></div>
          <div className="footer-bottom">
            <p className="copyright">© 2025 Visible Women LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainContent />} />
        <Route path="/become-sponsor" element={<SponsorForm />} />
        <Route path="/attend-events" element={<EventsForm />} />
      </Routes>
    </Router>
  );
}

export default App;
